/**
 * One form hook, driven by a zod schema — LV §6, §126, §134.
 *
 * Every create/edit surface in this product had the same four things written by hand: a
 * `useState` holding the draft, a `set(key)(value)` curry, an `incomplete` boolean gating
 * the submit button, and a `mutationFn` that trimmed, normalised the German comma and
 * turned `""` into `null` on its way into the request body. Twenty-three files, and the
 * last of those four is the one that matters — it is where LV §117's decimal discipline
 * lives, and it was restated per form.
 *
 * This hook keeps the first two exactly as they were, so a conversion is mechanical, and
 * takes the other two off the call site: the schema decides what is complete, and the
 * schema's *output* is the request body, already normalised.
 *
 * ## What it does not do
 *
 * It does not decide anything. The schema describes shape (see `fields.ts`); the server
 * decides whether the values are allowed, and its refusal is routed back to the field it
 * is about with `setServerError`. A `422` is never re-derived here (LV §134, §223).
 *
 * ## When an error becomes visible
 *
 * After the field has been **edited and then left**, or after a submit is attempted — or
 * at once, for a non-empty value the form was opened with that already fails, since the
 * disabled submit could not be pressed to reveal it.
 *
 * Not while it is being typed in — that paints a required field red at its first
 * character. And not on blur alone, which is the version this hook shipped with for
 * about an hour: `Modal`'s focus trap focuses the first input on mount and restores
 * focus on cleanup, so the Materialnummer of every freshly opened create form carried
 * "Pflichtfeld." with the cursor still sitting in it. Found by rendering the page; no
 * unit test saw it, because the unit test rendered the field without the dialog.
 *
 * The underlying rule was wrong rather than the environment: a field the *dialog* put
 * the cursor in, and that the user tabbed straight out of, is not a field the user has
 * said anything about. "Edited and then left" needs no exception for focus management.
 *
 * A server error is visible the moment it arrives and disappears as soon as that field
 * is edited, because the refusal was about the value that has now changed.
 *
 * ## Rows, and controls that are not an input
 *
 * A checklist answer or a criticality weight is not a top-level key: it sits at
 * `answers.<item>.numeric` or `weights.2.weight_percent`. `setAt`, `blur` and `errorAt`
 * take such a path, and the visibility rule is the same one, applied to the path rather
 * than to its first segment — otherwise every row would turn red the moment one row was
 * edited and left. The same three serve a control that has no `value`/`onChange` pair to
 * spread, such as a picker or a multiple choice, with a one-segment path.
 */

import { useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import type { z } from "zod";

import { deutscheFehler } from "./messages";

/** The keys of `In` whose value is a string — the ones `field()` can drive. */
type Textfelder<In> = {
  [K in keyof In]: In[K] extends string ? K : never;
}[keyof In];

/** The keys of `In` whose value is a boolean — the ones `checkbox()` can drive. */
type Schalter<In> = {
  [K in keyof In]: In[K] extends boolean ? K : never;
}[keyof In];

/** The keys of `In` that hold a chosen file — the ones `file()` can drive. */
type Dateifelder<In> = {
  [K in keyof In]: In[K] extends File | null ? K : never;
}[keyof In];

/** Where a value sits in the draft: a top-level key, then array indices or record keys. */
export type Pfad = ReadonlyArray<string | number>;

/** The key a path is remembered under. A one-segment path is its top-level key. */
function schluesselVon(pfad: Pfad): string {
  return pfad.map(String).join(".");
}

/** `ziel` with the value at `pfad` replaced, every object on the way copied. */
function setzeTief(ziel: unknown, pfad: Pfad, wert: unknown): unknown {
  if (pfad.length === 0) return wert;
  const [kopf, ...rest] = pfad;
  if (Array.isArray(ziel)) {
    const liste: readonly unknown[] = ziel;
    const kopie = [...liste];
    kopie[Number(kopf)] = setzeTief(liste[Number(kopf)], rest, wert);
    return kopie;
  }
  const basis = (ziel ?? {}) as Record<string, unknown>;
  return { ...basis, [String(kopf)]: setzeTief(basis[String(kopf)], rest, wert) };
}

/** The value at `pfad` in `ziel`, or `undefined` where the path does not exist (yet). */
function wertBei(ziel: unknown, pfad: Pfad): unknown {
  let aktuell = ziel;
  for (const teil of pfad) {
    if (aktuell === null || typeof aktuell !== "object") return undefined;
    aktuell = (aktuell as Record<string, unknown>)[String(teil)];
  }
  return aktuell;
}

/** `aktuell` without the refusals at `schluessel`, below it, or on the way to it. */
function ohneServerFehler(
  aktuell: Record<string, string>,
  schluessel: string,
): Record<string, string> {
  const betroffen = (anderer: string) =>
    anderer === schluessel ||
    anderer.startsWith(`${schluessel}.`) ||
    schluessel.startsWith(`${anderer}.`);
  if (!Object.keys(aktuell).some(betroffen)) return aktuell;
  return Object.fromEntries(
    Object.entries(aktuell).filter(([anderer]) => !betroffen(anderer)),
  );
}

/** What a text input, select or textarea needs. Spread it. */
export interface Feldbindung {
  value: string;
  onChange: (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => void;
  onBlur: () => void;
}

/** What a checkbox needs. Spread it. */
export interface Schalterbindung {
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

/**
 * What a file input needs. Spread it.
 *
 * There is no `value`, and that is not an omission. A file input cannot be controlled:
 * assigning to `input.value` is blocked by every browser, because a page that could put
 * a path there could read a file the user never offered. So the DOM keeps the selection
 * and the draft keeps the `File` — the one place in this hook where the two are not the
 * same object, and the reason `reset()` cannot clear a chosen file from the widget.
 */
export interface Dateibindung {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

export interface SchemaFormular<In, Out> {
  /** The draft, as typed. Strings stay strings until the schema transforms them. */
  values: In;
  /** Per-field German messages, only for fields whose error should be visible yet. */
  errors: Partial<Record<keyof In, string>>;
  /** A refusal that belongs to the form rather than to one field. */
  formError?: string;
  /** Whether the draft parses. Drives `submitDisabled`. */
  isValid: boolean;
  /** Whether anything has changed since the last baseline. Feeds `useUnsavedChangesWarning`. */
  isDirty: boolean;
  /** The curry the hand-written forms already used, kept so conversions stay small. */
  set: <K extends keyof In>(key: K) => (value: In[K]) => void;
  /** Props for a text input, select or textarea. */
  field: (key: Textfelder<In>) => Feldbindung;
  /** Props for a checkbox. */
  checkbox: (key: Schalter<In>) => Schalterbindung;
  /** Props for a file input. */
  file: (key: Dateifelder<In>) => Dateibindung;
  /** Parse, and run `senden` with the request body if it parses. */
  submit: (senden: (body: Out) => void) => void;
  /**
   * Set a value below the top level — a row's answer, one weight of several — or a
   * top-level value from a control that has no event to spread. Marks that path edited.
   */
  setAt: (...pfad: Pfad) => (value: unknown) => void;
  /** The `onBlur` for a path, for a control `field()` cannot bind. */
  blur: (...pfad: Pfad) => () => void;
  /** The message to show at a path, under the same rule as `errors`. */
  errorAt: (...pfad: Pfad) => string | undefined;
  /** Route a server refusal onto the field it is about — a key, or a path below one. */
  setServerError: (key: keyof In | Pfad, message: string) => void;
  /** Re-baseline the draft — after a save, or when the edited entity changes. */
  reset: (next?: In) => void;
}

export function useSchemaForm<In extends Record<string, unknown>, Out>(
  schema: z.ZodType<Out, z.ZodTypeDef, In>,
  initial: In,
): SchemaFormular<In, Out> {
  const [values, setValues] = useState<In>(initial);
  // Two sets, not one: an error needs both. See the note above about the focus trap.
  const [bearbeitet, setBearbeitet] = useState<ReadonlySet<string>>(new Set());
  const [verlassen, setVerlassen] = useState<ReadonlySet<string>>(new Set());
  const [abgeschickt, setAbgeschickt] = useState(false);
  const [serverFehler, setServerFehler] = useState<Record<string, string>>({});

  // The baseline `isDirty` is measured against. A ref, not state: changing it must not
  // re-render, and `reset` is the only thing that moves it.
  const grundzustand = useRef<In>(initial);

  const ergebnis = useMemo(
    () => schema.safeParse(values, { errorMap: deutscheFehler }),
    [schema, values],
  );

  const [alleFehler, allePfadFehler] = useMemo(() => {
    const gesammelt: Record<string, string> = {};
    const jePfad: Record<string, string> = {};
    if (ergebnis.success) return [gesammelt, jePfad];
    for (const issue of ergebnis.error.issues) {
      const schluessel = issue.path.length === 0 ? "" : String(issue.path[0]);
      // First issue per field wins. A field with two complaints shows the first, and
      // showing both would need a list where the design system has one slot.
      if (!(schluessel in gesammelt)) gesammelt[schluessel] = issue.message;
      const pfad = schluesselVon(issue.path);
      if (!(pfad in jePfad)) jePfad[pfad] = issue.message;
    }
    return [gesammelt, jePfad];
  }, [ergebnis]);

  const errors = useMemo(() => {
    const sichtbar: Record<string, string> = {};
    for (const [schluessel, meldung] of Object.entries(alleFehler)) {
      if (schluessel === "") continue;
      const bearbeitetUndVerlassen =
        bearbeitet.has(schluessel) && verlassen.has(schluessel);
      // A value the form was opened with, not typed by the user, that already fails: a
      // year outside the server's range that came in through the import. Waiting for
      // "edited and left" would leave "Speichern" disabled with nothing saying why — the
      // button cannot be pressed to reveal it. An empty start value is not this case: a
      // fresh create form is empty by definition (#316).
      const start = grundzustand.current[schluessel as keyof In];
      const vorbelegtUndFalsch =
        typeof start === "string" &&
        start.trim() !== "" &&
        values[schluessel as keyof In] === start;
      if (abgeschickt || bearbeitetUndVerlassen || vorbelegtUndFalsch) {
        sichtbar[schluessel] = meldung;
      }
    }
    // A server refusal outranks a shape complaint on the same field: it is the more
    // specific statement, and it is the one the user has not seen yet. A refusal below
    // the top level is `errorAt`'s, not this map's.
    for (const [schluessel, meldung] of Object.entries(serverFehler)) {
      if (!schluessel.includes(".")) sichtbar[schluessel] = meldung;
    }
    return sichtbar as Partial<Record<keyof In, string>>;
  }, [alleFehler, abgeschickt, bearbeitet, verlassen, serverFehler, values]);

  const bearbeitetMerken = (schluessel: string): void => {
    setBearbeitet((aktuell) => {
      if (aktuell.has(schluessel)) return aktuell;
      const naechster = new Set(aktuell);
      naechster.add(schluessel);
      return naechster;
    });
    // The refusal was about the value that has just changed.
    setServerFehler((aktuell) => ohneServerFehler(aktuell, schluessel));
  };

  const setzen = <K extends keyof In>(key: K) => {
    return (value: In[K]): void => {
      setValues((aktuell) => ({ ...aktuell, [key]: value }));
      bearbeitetMerken(String(key));
    };
  };

  const setzenUnter =
    (...pfad: Pfad) =>
    (value: unknown): void => {
      setValues((aktuell) => setzeTief(aktuell, pfad, value) as In);
      bearbeitetMerken(schluesselVon(pfad));
    };

  const verlassenUnter =
    (...pfad: Pfad) =>
    (): void => {
      const schluessel = schluesselVon(pfad);
      setVerlassen((aktuell) => {
        if (aktuell.has(schluessel)) return aktuell;
        const naechster = new Set(aktuell);
        naechster.add(schluessel);
        return naechster;
      });
    };

  const verlassenMerken = (key: keyof In) => verlassenUnter(String(key));

  return {
    values,
    errors,
    formError: abgeschickt ? alleFehler[""] : undefined,
    isValid: ergebnis.success,
    isDirty: Object.keys(values).some(
      (key) => values[key] !== grundzustand.current[key],
    ),
    set: setzen,
    field: (key) => ({
      value: values[key] as string,
      onChange: (event) => setzen(key)(event.target.value as In[typeof key]),
      onBlur: verlassenMerken(key),
    }),
    checkbox: (key) => ({
      checked: values[key] as boolean,
      onChange: (event) => setzen(key)(event.target.checked as In[typeof key]),
      onBlur: verlassenMerken(key),
    }),
    file: (key) => ({
      onChange: (event) =>
        setzen(key)((event.target.files?.[0] ?? null) as In[typeof key]),
      onBlur: verlassenMerken(key),
    }),
    setAt: setzenUnter,
    blur: verlassenUnter,
    errorAt: (...pfad) => {
      const schluessel = schluesselVon(pfad);
      if (schluessel in serverFehler) return serverFehler[schluessel];
      // The same "opened with a value that already fails" rule as `errors`, per path: a
      // checklist draft restored from the device whose reading an older build accepted.
      const start = wertBei(grundzustand.current, pfad);
      const vorbelegtUndFalsch =
        typeof start === "string" &&
        start.trim() !== "" &&
        wertBei(values, pfad) === start;
      const sichtbar =
        abgeschickt ||
        (bearbeitet.has(schluessel) && verlassen.has(schluessel)) ||
        vorbelegtUndFalsch;
      return sichtbar ? allePfadFehler[schluessel] : undefined;
    },
    submit: (senden) => {
      setAbgeschickt(true);
      if (ergebnis.success) senden(ergebnis.data);
    },
    setServerError: (key, message) => {
      const schluessel = Array.isArray(key) ? schluesselVon(key) : String(key);
      setServerFehler((aktuell) => ({ ...aktuell, [schluessel]: message }));
    },
    reset: (next) => {
      const ziel = next ?? grundzustand.current;
      grundzustand.current = ziel;
      setValues(ziel);
      setBearbeitet(new Set());
      setVerlassen(new Set());
      setAbgeschickt(false);
      setServerFehler({});
    },
  };
}
